import { Menu as BaseMenu } from "@base-ui/react/menu";
import { useState } from "react";
import { Input } from "../../Input";
import { IconChevronLeft } from "../../icons";
import type { MenuItemProps } from "..";
import { MenuSearchItem } from "../MenuSearchItem";
import { MenuSubitem } from "../MenuSubitem";
import {
  itemsGridClass,
  menuPopupContainerClass,
  modalHeaderClass,
  nothingFoundSpanClass,
  searchDivClass,
  styledMenuPositionerClass,
} from "../styles.css";

interface MenuModalProps {
  item: MenuItemProps;
  menuItems: MenuItemProps[];
  onClose: () => void;
}

export const formatSearchItems = (
  menuItems: MenuItemProps[],
  parentPath = "",
  parentTexts: string[] = [],
): MenuItemProps[] => {
  return menuItems
    .filter(
      (item) =>
        !item.filler && item.condition !== false && !item.id.includes("search"),
    )
    .flatMap((item) => {
      const currentPath = parentPath + (item.path || "");
      const currentTexts = [...parentTexts, item.text];

      if (item.subitems && item.subitems.length > 0) {
        return formatSearchItems(item.subitems, currentPath, currentTexts);
      }

      return [
        {
          ...item,
          path: currentPath,
          searchText: currentTexts.join("/"),
        },
      ];
    });
};

const formatMenuItems = (item: MenuItemProps): MenuItemProps => {
  const flattenedSubitems = item.subitems
    ?.filter((subitem) => !subitem.filler && subitem.condition !== false)
    .flatMap((subitem) => {
      const parentPath = item.path || "";
      const currentPath = parentPath + (subitem.path || "");

      if (!subitem.subitems) {
        return { ...subitem, path: currentPath };
      }

      const filteredSubsubitems = subitem.subitems.filter(
        (subsubitem) => !subsubitem.filler && subsubitem.condition !== false,
      );

      const formattedSubsubitems = filteredSubsubitems.map((subsubitem) => {
        return {
          ...subsubitem,
          path: currentPath + (subsubitem.path || ""),
          isSubsubitem: true,
          isLastSubsubitem:
            subsubitem.id === filteredSubsubitems.slice(-1)[0].id,
        };
      });

      return [
        {
          ...subitem,
          path:
            currentPath + (subitem.subitems ? subitem.subitems[0].path : ""),
        },
        ...formattedSubsubitems,
      ];
    });

  return { ...item, subitems: flattenedSubitems };
};

const filterItems = (item: MenuItemProps, search: string) => {
  return item.searchText?.toLowerCase().includes(search.toLowerCase());
};

export const MenuModal = ({ item, menuItems, onClose }: MenuModalProps) => {
  const [search, setSearch] = useState("");
  const searchItemsFormatted: MenuItemProps[] = formatSearchItems(menuItems);
  const searchItemsFiltered = searchItemsFormatted.filter((item) =>
    filterItems(item, search),
  );
  const itemFormatted = formatMenuItems(item);

  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        className={styledMenuPositionerClass}
        side="right"
        align="start"
        sideOffset={0}
      >
        <BaseMenu.Popup className={menuPopupContainerClass}>
          <span className={modalHeaderClass} onClick={onClose}>
            <IconChevronLeft />
            {item.text}
          </span>
          {item.id.includes("search") ? (
            <>
              <div className={searchDivClass}>
                <Input
                  autoFocus
                  label="Buscar"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {search.length > 0 &&
                (searchItemsFiltered.length > 0 ? (
                  searchItemsFiltered.map((item: any) => (
                    <MenuSearchItem
                      key={item.id}
                      subsubitem={item}
                      parentPath=""
                    />
                  ))
                ) : (
                  <span className={nothingFoundSpanClass}>Nada encontrado</span>
                ))}
            </>
          ) : (
            <div className={itemsGridClass}>
              {itemFormatted.subitems?.map((subitem) => {
                return <MenuSubitem key={subitem.id} subitem={subitem} />;
              })}
            </div>
          )}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
};
